/** @jsxImportSource @emotion/react */
import {
  CloseOutlined,
  RollbackOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Modal, Button, Tooltip } from 'antd'
import {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react'
// import Text from 'src/components/ts-components/Text'
import { useModalScroll } from 'src/hooks/useModalScroll'
import useWindowSize from 'src/hooks/useWindowSize'
import Brackets from '../Brackets'
import req from '@sportninja/common/api/request'
import {
  findStats,
  separateUpperAndLowerPools,
} from '@sportninja/common/utils/stats'
import { translateStatsResponse } from '@sportninja/common/utils/stats'

const styles = css`
  .my-modal-body {
  }
  .my-modal-mask {
    backdrop-filter: blur(1px);
  }
  .my-modal-header {
    border-bottom: 1px dotted ${colors.FIERY_RED};
  }
  .my-modal-footer {
    color: ${colors.FIERY_RED};
  }
  .my-modal-content {
    border: 1px solid #333;
  }
`

const classNames = {
  body: styles['my-modal-body'],
  mask: styles['my-modal-mask'],
  header: styles['my-modal-header'],
  footer: styles['my-modal-footer'],
  content: styles['my-modal-content'],
}

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    // borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    // borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '0',
    paddingBottom: '0',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10002,
    width: '100vw',
    height: '100vh',
  },
  mask: {
    backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10001,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

type FullScreenBracketModalProps = {
  isOpen: boolean
  onClose: () => void
  scheduleId: string
  division: any
  competitionType: string
}

export const Separator = () => {
  return (
    <div
      css={css`
        border-bottom: 1px solid #3f4753;
      `}
    />
  )
}

const FullScreenBracketModal = ({
  isOpen,
  onClose,
  scheduleId,
  // division,
  competitionType,
}: FullScreenBracketModalProps) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const { openModal, closeModal } = useModalScroll()
  const [rounds, setRounds] = useState([])
  const [stats, setStats] = useState({})
  const [selected, setSelected] = useState({})
  const [zoomLevel, setZoomLevel] = useState(1)
  const parentRef = useRef(null)
  const innerRef = useRef(null)
  const [loadingBracket, setLoadingBracket] = useState(false)

  useEffect(() => {
    if (isOpen) {
      openModal()
    } else {
      closeModal()
    }
  }, [closeModal, isOpen, openModal])

  const onScroll = useCallback((e) => {
    // @ts-ignore
    const availableWidth = parentRef?.current?.clientWidth
    // @ts-ignore
    const innerWidth = innerRef?.current?.clientWidth

    const left = e.target.scrollLeft
    const availableScroll = innerWidth - availableWidth

    if (availableWidth > innerWidth) {
      // @ts-ignore
      parentRef?.current?.classList.remove('show-left-fade')
      // @ts-ignore
      parentRef?.current?.classList.remove('show-right-fade')
    } else if (left >= availableScroll) {
      e.target.classList.remove('show-right-fade')
      e.target.classList.add('show-left-fade')
    } else if (left === 0) {
      e.target.classList.remove('show-left-fade')
      e.target.classList.add('show-right-fade')
    } else if (left !== 0 || left < availableScroll) {
      e.target.classList.add('show-right-fade')
      e.target.classList.add('show-left-fade')
    }
  }, [])

  const checkScrollRoom = useCallback(() => {
    // @ts-ignore
    const availableWidth = parentRef?.current?.clientWidth
    // @ts-ignore
    const innerWidth = innerRef?.current?.clientWidth

    if (availableWidth < innerWidth) {
      // @ts-ignore
      parentRef?.current?.classList.add('show-right-fade')
    } else {
      // @ts-ignore
      parentRef?.current?.classList.remove('show-right-fade')
    }
  }, [])

  useEffect(() => {
    if (!loadingBracket) {
      checkScrollRoom()
    }
  }, [checkScrollRoom, loadingBracket])

  const handle = (response) => {
    let finalRounds = response?.data.map((round) => {
      const pools = round?.pools?.data?.map((pool) => {
        // @ts-ignore
        const ref = createRef(null)
        return {
          ref,
          ...pool,
        }
      })
      return {
        ...round,
        pools,
      }
    })
    finalRounds = separateUpperAndLowerPools(finalRounds)
    setRounds(finalRounds)
  }

  const handleStats = (response) => {
    if (!response?.data) {
      return
    }
    const aggregatedStats = translateStatsResponse(response)
    const teamsByPool = {}
    aggregatedStats.forEach((teamPerPool) => {
      const { team, stats, pool } = teamPerPool
      const wins = findStats(stats, 'win')
      const losses = findStats(stats, 'loss')
      const ties = findStats(stats, 'tie')
      const points = findStats(stats, 'points')
      teamsByPool[pool.id] = Object.assign({}, teamsByPool[pool.id], {
        [team.id]: {
          scoreString: `${wins}-${losses}-${ties}`,
          points,
        },
      })
    })

    setStats(teamsByPool)
  }

  useEffect(() => {
    const combined = async () => {
      try {
        setLoadingBracket(true)
        const results = await Promise.all([
          await req(`/schedules/${scheduleId}/tournaments`),
          await req(`/schedules/${scheduleId}/tournaments/stats`),
        ])
        typeof results[0] !== 'undefined' && handle(results[0])
        typeof results[1] !== 'undefined' && handleStats(results[1])
        setLoadingBracket(false)
      } catch (e) {
        console.error(e)
        setLoadingBracket(false)
      }
    }
    if (scheduleId && isOpen) {
      combined()
    }
  }, [scheduleId, isOpen])

  // const getHeader = useCallback(() => {
  //   return (
  //     <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
  //       {division?.parent?.name} - {division?.name}
  //     </Text>
  //   )
  // }, [division])

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 18px;
          cursor: pointer;
          transition: all 0.2s;
          &:hover {
            color: ${colors.WHITE};
          }
        `}
      />
    )
  }, [])

  const handleZoomIn = useCallback(() => {
    setZoomLevel((prev) => Math.min(prev + 0.1, 2))
    setTimeout(() => {
      checkScrollRoom()
    }, 100)
  }, [checkScrollRoom])

  const handleZoomOut = useCallback(() => {
    setZoomLevel((prev) => Math.max(prev - 0.1, 0.5))
    setTimeout(() => {
      checkScrollRoom()
    }, 100)
  }, [checkScrollRoom])

  const isAtMinimumZoom = useMemo(() => zoomLevel <= 0.5, [zoomLevel])
  const zoomLevelPercentage = useMemo(() => {
    const percentage = 100 / zoomLevel
    return percentage < 125 && percentage > 100 ? 125 : percentage
  }, [zoomLevel])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        background: ${colors.SECONDARY_400} !important;
        height: ${isMobile ? '100vh' : '96vh'} !important;
        max-height: 2160px;
        margin: ${isMobile ? '0' : '0'};
        padding: 0;
        width: 100vw;
        max-width: ${isMobile ? '100%' : '3840px'};
        border-radius: ${isMobile ? '0' : '8px'} !important;
        top: ${isMobile ? '0' : '50%'};
        overflow: hidden;
        .ant-modal-content {
          height: ${isMobile ? '100vh' : '96vh'} !important;
          max-height: 2160px;
          border-radius: ${isMobile ? '0' : '8px'} !important;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          position: relative;
          width: 100%;
        }
        .ant-modal-body {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          position: relative;
          padding: 0;
          width: 100%;
        }
        .ant-modal-title {
          padding: 8px !important;
        }
        .zoom-controls {
          position: absolute;
          bottom: 24px;
          right: 24px;
          display: flex;
          gap: 8px;
          z-index: 1000;
        }
        .zoom-button {
          background-color: ${colors.SECONDARY_400};
          border-color: ${colors.PRIMARY};
          color: ${colors.WHITE};
          &:hover {
            background-color: ${colors.SECONDARY_300};
            border-color: ${colors.PRIMARY};
            color: ${colors.WHITE};
          }
        }
      `}
      width={isMobile ? '100%' : '95vw'}
      closeIcon={closeIcon()}
      // title={getHeader()}
      title={null}
      footer={null}
      open={isOpen}
      onCancel={() => {
        onClose()
        setZoomLevel(1)
      }}
      centered={!isMobile}
      maskClosable={false}
      classNames={classNames}
      styles={modalStyles}
    >
      <div
        css={css`
          position: relative;
          height: 100%;
          display: flex;
          flex-direction: column;
          width: 100%;
          overflow: hidden;
        `}
      >
        <div
          css={css`
            position: relative;
            height: 100%;
            width: 100%;
            overflow: ${isAtMinimumZoom ? 'hidden' : 'auto'};
            -webkit-overflow-scrolling: touch;
          `}
          ref={parentRef}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 16px;
              padding: 16px;
              width: ${zoomLevel < 1 ? `${zoomLevelPercentage}%` : '100%'};
              min-width: ${zoomLevel < 1 ? `${zoomLevelPercentage}%` : '100%'};
              height: 100%;
              transform: scale(${zoomLevel});
              transform-origin: top left;
              transition: transform 0.2s ease-in-out;
              position: relative;
              overflow-y: visible;
              box-sizing: border-box;
              padding-bottom: ${zoomLevel > 1
                ? `${(zoomLevel - 1) * 200}px`
                : '16px'};
              padding-right: ${zoomLevel > 1
                ? `${(zoomLevel - 1) * 100}px`
                : '16px'};
            `}
            ref={innerRef}
          >
            <Brackets
              onScroll={onScroll}
              loadingBracket={loadingBracket}
              competitionType={competitionType}
              innerRef={innerRef}
              parentRef={parentRef}
              selectedComp={scheduleId}
              rounds={rounds}
              selected={selected}
              setSelected={setSelected}
              stats={stats}
              isOnModal={true}
              zoomLevel={zoomLevel}
            />
          </div>
        </div>
        <div className='zoom-controls'>
          {zoomLevel !== 1 ? (
            <Tooltip title='Reset Zoom' zIndex={20000}>
              <Button
                icon={<RollbackOutlined />}
                onClick={() => setZoomLevel(1)}
                className='zoom-button'
                aria-label='Zoom Out'
              />
            </Tooltip>
          ) : null}
          <Tooltip title='Zoom Out'>
            <Button
              icon={<ZoomOutOutlined />}
              onClick={handleZoomOut}
              className='zoom-button'
              aria-label='Zoom Out'
            />
          </Tooltip>
          <Tooltip title='Zoom In'>
            <Button
              icon={<ZoomInOutlined />}
              onClick={handleZoomIn}
              className='zoom-button'
              aria-label='Zoom In'
            />
          </Tooltip>
        </div>
      </div>
    </Modal>
  )
}

export default FullScreenBracketModal
